// ============================================================
// Datei: src/components/shiftOverview/ShiftOverviewMobile.jsx
//
// Zweck:
// - Mobile Deployment-Ansicht für verfügbare Schichten
// - Darstellung über Ressort-Karussell / Revolver
// - Tage über Tabs: Freitag, Samstag, Sonntag
// - Schichten als horizontale Karten
// - Awareness als eigenes Ressort ergänzt
// ============================================================

import React, { useEffect, useMemo, useRef, useState } from "react";
import Api from "../../Api";
import "./shiftOverviewMobile.css";

const MAX_ASSIGNED_SHIFTS = 3;
const ITEM_WIDTH = 120;

// ============================================================
// Reihenfolge der Tage für Zeitberechnung
// ============================================================
const DAY_INDEX = {
	Freitag: 0,
	Samstag: 1,
	Sonntag: 2
};

const DAYS = ["Freitag", "Samstag", "Sonntag"];

// ============================================================
// Chai-Untergruppen anhand der shift_id
//
// WICHTIG:
// - Zmorge ist hier bewusst NICHT enthalten.
// - Zmorge gehört jetzt zu Food.
// ============================================================
const CHAI_GROUPS = {
	KUCHEN_KAFFEE: {
		id: "Chai: Kuchen & Kaffee",
		labelPrefix: "Chai:",
		labelMain: "Kuchen & Kaffee",
		cssKey: "Chai",
		modalDept: "Chai",
		minShiftId: 301,
		maxShiftId: 307
	},
	CREPES: {
		id: "Chai: Crêpes",
		labelPrefix: "Chai:",
		labelMain: "Crêpes",
		cssKey: "Chai",
		modalDept: "Chai",
		minShiftId: 311,
		maxShiftId: 317
	}
};

// ============================================================
// Mobile Ressort-Liste
//
// Wichtig:
// - Es gibt bewusst KEIN einzelnes Department "Chai" mehr.
// - Stattdessen gibt es zwei Chai-Untergruppen.
// ============================================================
const DEPARTMENTS = [
	{
		id: "Bar",
		label: "Bar",
		cssKey: "Bar",
		modalDept: "Bar"
	},
	{
		id: "Caipibar",
		label: "Caipibar",
		cssKey: "Caipibar",
		modalDept: "Caipibar"
	},

	{
		id: CHAI_GROUPS.KUCHEN_KAFFEE.id,
		labelPrefix: CHAI_GROUPS.KUCHEN_KAFFEE.labelPrefix,
		labelMain: CHAI_GROUPS.KUCHEN_KAFFEE.labelMain,
		cssKey: CHAI_GROUPS.KUCHEN_KAFFEE.cssKey,
		modalDept: CHAI_GROUPS.KUCHEN_KAFFEE.modalDept,
		groupType: "CHAI_KUCHEN_KAFFEE"
	},

	{
		id: CHAI_GROUPS.CREPES.id,
		labelPrefix: CHAI_GROUPS.CREPES.labelPrefix,
		labelMain: CHAI_GROUPS.CREPES.labelMain,
		cssKey: CHAI_GROUPS.CREPES.cssKey,
		modalDept: CHAI_GROUPS.CREPES.modalDept,
		groupType: "CHAI_CREPES"
	},

	{
		id: "Food",
		label: "Food",
		cssKey: "Food",
		modalDept: "Food"
	},
	{
		id: "Infostand",
		label: "Infostand",
		cssKey: "Infostand",
		modalDept: "Infostand"
	},
	{
		id: "Awareness",
		label: "Awareness",
		cssKey: "Awareness",
		modalDept: "Awareness",
		aliases: ["Awareness", "Aweareness"]
	},
	{
		id: "Kaffimobil",
		label: "Kaffimobil",
		cssKey: "Kaffimobil",
		modalDept: "Kaffimobil"
	},
	{
		id: "Kasse",
		label: "Kasse",
		cssKey: "Kasse",
		modalDept: "Kasse"
	},
	{
		id: "Kindernami",
		label: "Kindernami",
		cssKey: "Kindernami",
		modalDept: "Kindernami"
	},
	{
		id: "Parkplatz",
		label: "Parkplatz",
		cssKey: "Parkplatz",
		modalDept: "Parkplatz"
	},
	{
		id: "Shuttle",
		label: "Schüttelbus",
		cssKey: "Schttelbus",
		modalDept: "Shuttle"
	},
	{
		id: "UmweltAmbiente",
		label: "Umwelt & Ambiente",
		cssKey: "UmweltAmbiente",
		modalDept: "UmweltAmbiente"
	}
];

const ShiftOverviewMobile = ({
	shifts = [],
	assignedShifts = [],
	handleJoinShift
}) => {
	// ============================================================
	// STATE
	// ============================================================
	const [activeDeptIndex, setActiveDeptIndex] = useState(0);
	const [activeDay, setActiveDay] = useState("Freitag");

	const [ressortDescriptions, setRessortDescriptions] = useState([]);
	const [activeDeptModal, setActiveDeptModal] = useState(null);

	const scrollRef = useRef(null);
	const dayAnchors = useRef({});

	// ============================================================
	// ZEITBERECHNUNG
	// Wird gebraucht, damit sich Helfer nicht für überschneidende
	// Schichten einschreiben können.
	// ============================================================
	const toAbsoluteMinutes = (day, time) => {
		if (!time || !Object.prototype.hasOwnProperty.call(DAY_INDEX, day)) {
			return 0;
		}

		const [hours, minutes] = time.split(":").map(Number);

		return DAY_INDEX[day] * 1440 + hours * 60 + minutes;
	};

	const assignedIntervals = useMemo(() => {
		return assignedShifts
			.map((shift) => {
				const start = toAbsoluteMinutes(
					shift.dayOfDeployment,
					shift.startTime
				);

				let end = toAbsoluteMinutes(
					shift.dayOfDeployment,
					shift.endTime
				);

				if (end <= start) {
					end += 1440;
				}

				return {
					start,
					end
				};
			})
			.filter(Boolean);
	}, [assignedShifts]);

	const isTimeBlocked = (shift) => {
		const start = toAbsoluteMinutes(
			shift.dayOfDeployment,
			shift.startTime
		);

		let end = toAbsoluteMinutes(
			shift.dayOfDeployment,
			shift.endTime
		);

		if (end <= start) {
			end += 1440;
		}

		return assignedIntervals.some(
			(assigned) => start < assigned.end && assigned.start < end
		);
	};

	const maxReached = assignedShifts.length >= MAX_ASSIGNED_SHIFTS;

	// ============================================================
	// ÖFFENTLICHE SCHICHTEN
	// ============================================================
	const publicShifts = useMemo(() => {
		return shifts.filter((shift) => shift.shiftVisibility === "PUBLIC");
	}, [shifts]);

	// ============================================================
	// HILFSFUNKTIONEN FÜR CHAI-GRUPPEN
	// ============================================================
	const isShiftIdBetween = (shiftId, min, max) => {
		const numericShiftId = Number(shiftId);

		return numericShiftId >= min && numericShiftId <= max;
	};

	const isChaiKuchenKaffeeShift = (shift) => {
		return (
			shift.department === "Chai" &&
			isShiftIdBetween(
				shift.shiftId,
				CHAI_GROUPS.KUCHEN_KAFFEE.minShiftId,
				CHAI_GROUPS.KUCHEN_KAFFEE.maxShiftId
			)
		);
	};

	const isChaiCrepesShift = (shift) => {
		return (
			shift.department === "Chai" &&
			isShiftIdBetween(
				shift.shiftId,
				CHAI_GROUPS.CREPES.minShiftId,
				CHAI_GROUPS.CREPES.maxShiftId
			)
		);
	};

	// ============================================================
	// ORDNET EINE SCHICHT EINER MOBILEN RESSORT-KACHEL ZU
	//
	// Normale Ressorts:
	// - Vergleich über shift.department
	//
	// Chai:
	// - wird über shiftId in Untergruppen geteilt
	// ============================================================
	const belongsToDepartment = (shift, department) => {
		if (!shift || !department) {
			return false;
		}

		if (department.groupType === "CHAI_KUCHEN_KAFFEE") {
			return isChaiKuchenKaffeeShift(shift);
		}

		if (department.groupType === "CHAI_CREPES") {
			return isChaiCrepesShift(shift);
		}

		// Awareness robust erkennen:
		// Falls in der Datenbank/DTO versehentlich "Aweareness" steht,
		// wird die Schicht trotzdem in der Awareness-Kachel angezeigt.
		if (department.id === "Awareness") {
			const departmentName = shift.department || shift.departmentName || "";

			return (department.aliases || ["Awareness"]).includes(departmentName);
		}

		return shift.department === department.id || shift.departmentName === department.id;
	};

	// ============================================================
	// DEPARTMENTS FÜR MOBILE ANSICHT
	//
	// Wichtig:
	// - Chai wird hier nie als einzelnes Department übernommen.
	// - Die zwei Chai-Untergruppen bleiben immer getrennt.
	// ============================================================
	const departments = useMemo(() => {
		return DEPARTMENTS;
	}, []);

	useEffect(() => {
		if (activeDeptIndex > departments.length - 1) {
			setActiveDeptIndex(0);
		}
	}, [activeDeptIndex, departments.length]);

	const activeDept = departments[activeDeptIndex];

	// ============================================================
	// RESSORTBESCHREIBUNG LADEN
	//
	// Bei Chai-Untergruppen wird modalDept = "Chai" verwendet.
	// ============================================================
	const handleInfoClick = async (department) => {
		const descriptionDepartment = department.modalDept || department.id;

		try {
			const response = await Api.get(
				`/departments/${descriptionDepartment}/descriptions`
			);

			setRessortDescriptions(response.data || []);
			setActiveDeptModal(descriptionDepartment);
		} catch (error) {
			console.error("Fehler beim Laden der Ressortbeschreibung", error);

			setRessortDescriptions([]);
			setActiveDeptModal(descriptionDepartment);
		}
	};

	const closeDescriptionModal = () => {
		setActiveDeptModal(null);
		setRessortDescriptions([]);
	};

	// ============================================================
	// FREIE PLÄTZE PRO RESSORT-GRUPPE
	// ============================================================
	const departmentFreeSlots = useMemo(() => {
		const result = {};

		departments.forEach((department) => {
			result[department.id] = publicShifts
				.filter((shift) => belongsToDepartment(shift, department))
				.reduce((sum, shift) => {
					const available = Math.max(
						0,
						shift.publicSlots ?? shift.availableSlots ?? 0
					);

					return sum + available;
				}, 0);
		});

		return result;
	}, [departments, publicShifts]);

	// ============================================================
	// SCHICHTEN FÜR AKTIVES RESSORT + TAG
	// ============================================================
	const getPublicShiftsForCell = (department, day) => {
		if (!department) {
			return [];
		}

		return publicShifts
			.filter((shift) => {
				const available = Math.max(
					0,
					shift.publicSlots ?? shift.availableSlots ?? 0
				);

				return (
					belongsToDepartment(shift, department) &&
					shift.dayOfDeployment === day &&
					available > 0
				);
			})
			.sort(
				(a, b) =>
					toAbsoluteMinutes(a.dayOfDeployment, a.startTime) -
					toAbsoluteMinutes(b.dayOfDeployment, b.startTime)
			);
	};

	const hasNoPublishedShiftsAtAll = (department) => {
		return DAYS.every(
			(day) => getPublicShiftsForCell(department, day).length === 0
		);
	};

	const showDummiesForActiveDept = useMemo(() => {
		if (!activeDept) {
			return false;
		}

		return hasNoPublishedShiftsAtAll(activeDept);
	}, [activeDept, publicShifts]);

	const dayHasShifts = useMemo(() => {
		const result = {};

		if (!activeDept) {
			return result;
		}

		DAYS.forEach((day) => {
			result[day] = getPublicShiftsForCell(activeDept, day).length > 0;
		});

		return result;
	}, [activeDept, publicShifts]);

	const visibleTiles = useMemo(() => {
		if (!activeDept) {
			return [];
		}

		if (showDummiesForActiveDept) {
			return [
				{
					type: "dummy",
					id: `${activeDept.id}-dummy-${activeDay}`
				}
			];
		}

		return getPublicShiftsForCell(activeDept, activeDay).map((shift) => ({
			type: "shift",
			shift
		}));
	}, [activeDept, activeDay, publicShifts, showDummiesForActiveDept]);

	// ============================================================
	// AKTIVEN TAG AUTOMATISCH SETZEN
	// ============================================================
	useEffect(() => {
		if (!activeDept) {
			return;
		}

		if (showDummiesForActiveDept) {
			setActiveDay("Freitag");
			return;
		}

		const firstAvailableDay = DAYS.find((day) => dayHasShifts[day]);

		setActiveDay(firstAvailableDay || "Freitag");
	}, [activeDeptIndex, activeDept, showDummiesForActiveDept, dayHasShifts]);

	const scrollToDay = (day) => {
		if (!showDummiesForActiveDept && !dayHasShifts[day]) {
			return;
		}

		setActiveDay(day);

		const element = dayAnchors.current[day];

		if (element) {
			element.scrollIntoView({
				behavior: "smooth",
				inline: "center"
			});
		}
	};

	// ============================================================
	// LABEL RENDERN
	//
	// Normale Ressorts:
	// - einfache Ausgabe
	//
	// Chai-Untergruppen:
	// - Chai:
	// - Kuchen & Kaffee / Crêpes
	// ============================================================
	const renderDepartmentLabel = (department) => {
		if (!department.labelPrefix || !department.labelMain) {
			return <span className="dept-text">{department.label}</span>;
		}

		return (
			<span className="dept-text dept-text-grouped">
				<span className="dept-label-prefix">
					{department.labelPrefix}
				</span>
				<span className="dept-label-main">
					{department.labelMain}
				</span>
			</span>
		);
	};

	const trackOffset = -(activeDeptIndex * ITEM_WIDTH) + ITEM_WIDTH;

	if (!activeDept) {
		return (
			<div className="shift-mobile">
				<div className="shift-section">
					<div className="shift-section-card">
						<p className="shift-empty-text">
							Aktuell stehen keine Schichten zur Auswahl.
						</p>
					</div>
				</div>
			</div>
		);
	}

	// ============================================================
	// RENDER
	// ============================================================
	return (
		<div className="shift-mobile">
			<div className="dept-revolver">
				<button
					className="revolver-arrow"
					type="button"
					disabled={activeDeptIndex === 0}
					onClick={() => setActiveDeptIndex((index) => index - 1)}
				>
					‹
				</button>

				<div className="dept-viewport">
					<div
						className="dept-track"
						style={{ transform: `translateX(${trackOffset}px)` }}
					>
						{departments.map((department, index) => (
							<div
								key={department.id}
								className={`dept-item dept-${department.cssKey} ${
									index === activeDeptIndex ? "active" : ""
								}`}
								onClick={() => handleInfoClick(department)}
							>
								<div className="dept-info-icon">
									<img
										src={`${process.env.PUBLIC_URL}/icons/icon_info.svg`}
										alt="info"
									/>
								</div>

								{renderDepartmentLabel(department)}

								{departmentFreeSlots[department.id] > 0 && (
									<span className="dept-badge">
										{departmentFreeSlots[department.id]}
									</span>
								)}
							</div>
						))}
					</div>
				</div>

				<button
					className="revolver-arrow"
					type="button"
					disabled={activeDeptIndex >= departments.length - 1}
					onClick={() => setActiveDeptIndex((index) => index + 1)}
				>
					›
				</button>
			</div>

			<div className="day-tabs">
				{DAYS.map((day) => {
					const disabled =
						!showDummiesForActiveDept && !dayHasShifts[day];

					return (
						<button
							key={day}
							ref={(element) => (dayAnchors.current[day] = element)}
							className={`day-tab day-${day} ${
								day === activeDay ? "active" : ""
							} ${disabled ? "disabled" : ""}`}
							type="button"
							onClick={() => scrollToDay(day)}
							disabled={disabled}
						>
							{day}
						</button>
					);
				})}
			</div>

			<div className="shift-section">
				<div className="shift-section-card">
					<div className="shift-tile-grid" ref={scrollRef}>
						{visibleTiles.map((item) => {
							if (item.type === "dummy") {
								return (
									<div
										key={item.id}
										data-day={activeDay}
										className={`shift-card shift-card-dummy dept-${activeDept.cssKey}`}
									>
										<div className="shift-info">
											<p>Zurzeit stehen keine</p>
											<p>Schichten zur Auswahl</p>
										</div>
									</div>
								);
							}

							const shift = item.shift;

							const available = Math.max(
								0,
								shift.publicSlots ?? shift.availableSlots ?? 0
							);

							const disabled =
								available <= 0 ||
								maxReached ||
								isTimeBlocked(shift);

							return (
								<div
									key={shift.shiftId}
									data-day={shift.dayOfDeployment}
									className={`shift-card dept-${activeDept.cssKey} ${
										disabled ? "disabled" : ""
									}`}
								>
									<div className="shift-info">
										<p>
											{shift.startTime} – {shift.endTime}
										</p>
										<p>Verfügbar: {available}</p>
									</div>

									<button
										className="join-button"
										type="button"
										disabled={disabled}
										onClick={() => handleJoinShift(shift.shiftId)}
									>
										{disabled ? "Nicht möglich" : "Einschreiben"}
									</button>
								</div>
							);
						})}
					</div>
				</div>
			</div>

			{activeDeptModal && (
				<div className="ressort-description-modal">
					<div className="ressort-description-card">
						<h2>{activeDeptModal}</h2>

						{ressortDescriptions.map((description, index) => (
							<div key={index}>
								<h3>{description.title}</h3>
								<p>{description.description}</p>
							</div>
						))}

						<button
							className="close-description"
							type="button"
							onClick={closeDescriptionModal}
						>
							Schliessen
						</button>
					</div>
				</div>
			)}
		</div>
	);
};

export default ShiftOverviewMobile;